/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { workloadIdentitiesAdmitSubject } from "../funcs/workloadIdentitiesAdmitSubject.js";
import { workloadIdentitiesCreateDeviceJoinToken } from "../funcs/workloadIdentitiesCreateDeviceJoinToken.js";
import { workloadIdentitiesGetCustomFlows } from "../funcs/workloadIdentitiesGetCustomFlows.js";
import { workloadIdentitiesList } from "../funcs/workloadIdentitiesList.js";
import { workloadIdentitiesListPlatforms } from "../funcs/workloadIdentitiesListPlatforms.js";
import { workloadIdentitiesListTokenEndpoints } from "../funcs/workloadIdentitiesListTokenEndpoints.js";
import { workloadIdentitiesRegisterDevice } from "../funcs/workloadIdentitiesRegisterDevice.js";
import { workloadIdentitiesRegisterIssuer } from "../funcs/workloadIdentitiesRegisterIssuer.js";
import { workloadIdentitiesUpdateIssuer } from "../funcs/workloadIdentitiesUpdateIssuer.js";
import { workloadIdentitiesUpdateSubject } from "../funcs/workloadIdentitiesUpdateSubject.js";
import { workloadIdentitiesWithdrawIssuer } from "../funcs/workloadIdentitiesWithdrawIssuer.js";
import { workloadIdentitiesWithdrawSubject } from "../funcs/workloadIdentitiesWithdrawSubject.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { RegisterDeviceRequestBody } from "../models/components/registerdevicerequestbody.js";
import { WorkloadCustomFlows } from "../models/components/workloadcustomflows.js";
import { WorkloadDeviceJoinToken } from "../models/components/workloaddevicejointoken.js";
import { WorkloadDeviceRegistration } from "../models/components/workloaddeviceregistration.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { WorkloadPlatformCatalog } from "../models/components/workloadplatformcatalog.js";
import { WorkloadTokenEndpoints } from "../models/components/workloadtokenendpoints.js";
import {
  AdmitWorkloadSubjectRequest,
  AdmitWorkloadSubjectSecurity,
} from "../models/operations/admitworkloadsubject.js";
import {
  CreateWorkloadDeviceJoinTokenRequest,
  CreateWorkloadDeviceJoinTokenSecurity,
} from "../models/operations/createworkloaddevicejointoken.js";
import {
  GetWorkloadCustomFlowsRequest,
  GetWorkloadCustomFlowsSecurity,
} from "../models/operations/getworkloadcustomflows.js";
import {
  ListWorkloadIdentitiesRequest,
  ListWorkloadIdentitiesSecurity,
} from "../models/operations/listworkloadidentities.js";
import {
  ListWorkloadPlatformsRequest,
  ListWorkloadPlatformsSecurity,
} from "../models/operations/listworkloadplatforms.js";
import {
  ListWorkloadTokenEndpointsRequest,
  ListWorkloadTokenEndpointsSecurity,
} from "../models/operations/listworkloadtokenendpoints.js";
import {
  RegisterWorkloadIssuerRequest,
  RegisterWorkloadIssuerSecurity,
} from "../models/operations/registerworkloadissuer.js";
import {
  UpdateWorkloadIssuerRequest,
  UpdateWorkloadIssuerSecurity,
} from "../models/operations/updateworkloadissuer.js";
import {
  UpdateWorkloadSubjectRequest,
  UpdateWorkloadSubjectSecurity,
} from "../models/operations/updateworkloadsubject.js";
import {
  WithdrawWorkloadIssuerRequest,
  WithdrawWorkloadIssuerSecurity,
} from "../models/operations/withdrawworkloadissuer.js";
import {
  WithdrawWorkloadSubjectRequest,
  WithdrawWorkloadSubjectSecurity,
} from "../models/operations/withdrawworkloadsubject.js";
import { unwrapAsync } from "../types/fp.js";

export class WorkloadIdentities extends ClientSDK {
  /**
   * admitSubject workloadIdentities
   *
   * @remarks
   * Admit a subject one of the trusted issuers asserts, and assign the agent it inherits its policy from. Both happen in one transaction: a subject admitted without an agent is refused at the token endpoint, so that half-configured state is not reachable. Requires workload:write.
   */
  async admitSubject(
    request: AdmitWorkloadSubjectRequest,
    security?: AdmitWorkloadSubjectSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesAdmitSubject(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createDeviceJoinToken workloadIdentities
   *
   * @remarks
   * Create a single-use join token that enrolls one headless box with no platform identity as a workload assigned to an agent. The token is returned once and only its hash is kept. Only a person can create one: a key backed by a principal rather than a user is refused with 403 whatever its grants. Requires workload:write, and either owning the agent or agent:authorize on it, since an enrolled box acts under the agent's policy.
   */
  async createDeviceJoinToken(
    request: CreateWorkloadDeviceJoinTokenRequest,
    security?: CreateWorkloadDeviceJoinTokenSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadDeviceJoinToken> {
    return unwrapAsync(workloadIdentitiesCreateDeviceJoinToken(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getCustomFlows workloadIdentities
   *
   * @remarks
   * Get the forms for trusting a platform the catalog does not list and allowing its workloads: registering and editing a trusted platform, and allowing and editing access. The same for every organization. Requires workload:read.
   */
  async getCustomFlows(
    request?: GetWorkloadCustomFlowsRequest | undefined,
    security?: GetWorkloadCustomFlowsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadCustomFlows> {
    return unwrapAsync(workloadIdentitiesGetCustomFlows(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * list workloadIdentities
   *
   * @remarks
   * Read the whole trust policy: every trusted issuer and every admitted subject at the organization tier, plus the selected project's tier when the caller names a project, with the agent each subject resolves to. Requires workload:read.
   */
  async list(
    request?: ListWorkloadIdentitiesRequest | undefined,
    security?: ListWorkloadIdentitiesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listPlatforms workloadIdentities
   *
   * @remarks
   * List the platforms the catalog offers to trust without looking anything up, each with the guided setup that connects it. The same for every organization. Requires workload:read.
   */
  async listPlatforms(
    request?: ListWorkloadPlatformsRequest | undefined,
    security?: ListWorkloadPlatformsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadPlatformCatalog> {
    return unwrapAsync(workloadIdentitiesListPlatforms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listTokenEndpoints workloadIdentities
   *
   * @remarks
   * List the token endpoints an external platform can be pointed at: one per user session issuer in shared mode, at the organization level and in each project, with the issuer it serves. Issuers this deployment does not serve a shared authorization server for are left out. Requires workload:read.
   */
  async listTokenEndpoints(
    request?: ListWorkloadTokenEndpointsRequest | undefined,
    security?: ListWorkloadTokenEndpointsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadTokenEndpoints> {
    return unwrapAsync(workloadIdentitiesListTokenEndpoints(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * registerDevice workloadIdentities
   *
   * @remarks
   * Enroll a device with its public key, redeeming a join token. No session or API key: the single-use join token is the credential, and an unknown, expired, or already redeemed token returns 401. A key already registered to another device returns 409, and an agent whose policy no longer grants org:device_agent_sync returns a failed precondition without spending the token. In one transaction the device's key is registered, the device is admitted under the organization's Speakeasy device issuer, and it is assigned the token's agent.
   */
  async registerDevice(
    request: RegisterDeviceRequestBody,
    options?: RequestOptions,
  ): Promise<WorkloadDeviceRegistration> {
    return unwrapAsync(workloadIdentitiesRegisterDevice(
      this,
      request,
      options,
    ));
  }

  /**
   * registerIssuer workloadIdentities
   *
   * @remarks
   * Trust an external issuer to vouch for workloads. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
   */
  async registerIssuer(
    request: RegisterWorkloadIssuerRequest,
    security?: RegisterWorkloadIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesRegisterIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateIssuer workloadIdentities
   *
   * @remarks
   * Edit a trusted issuer's name, description, tags, or JWKS URI. Omitted fields are left unchanged. The issuer URL and the wildcard admission setting are fixed at registration. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
   */
  async updateIssuer(
    request: UpdateWorkloadIssuerRequest,
    security?: UpdateWorkloadIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesUpdateIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateSubject workloadIdentities
   *
   * @remarks
   * Edit an admitted subject's label, tags, or assigned agent. Omitted fields are left unchanged. The subject, match kind, issuer, and tier are fixed at admission. The agent assignment is shared by every admission of the same subject under the same issuer, at either tier, so reassigning it through one admission reassigns it for both. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
   */
  async updateSubject(
    request: UpdateWorkloadSubjectRequest,
    security?: UpdateWorkloadSubjectSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesUpdateSubject(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * withdrawIssuer workloadIdentities
   *
   * @remarks
   * Stop trusting an issuer. Every subject admitted under it is withdrawn in the same transaction, so no admission can outlive the issuer it names. Requires workload:write.
   */
  async withdrawIssuer(
    request: WithdrawWorkloadIssuerRequest,
    security?: WithdrawWorkloadIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesWithdrawIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * withdrawSubject workloadIdentities
   *
   * @remarks
   * Withdraw an admitted subject and its agent assignment, stopping it authenticating. Requires workload:write.
   */
  async withdrawSubject(
    request: WithdrawWorkloadSubjectRequest,
    security?: WithdrawWorkloadSubjectSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkloadIdentityPolicy> {
    return unwrapAsync(workloadIdentitiesWithdrawSubject(
      this,
      request,
      security,
      options,
    ));
  }
}
